import type {
  AeroLiftingLineSolveResponse,
  AeroSectionSolveResponse,
  AeroSectionSolveRow,
} from "../../api/aeroWorkbench";

export interface FoilMemoryPredictionReceipt {
  kind?: "global-exact" | "solver-run" | string;
  exact?: boolean;
  approximate?: boolean;
  scope?: string;
  observation_key?: string;
  solver_backend?: string;
  solver_build_id?: string;
  // Compatibility names accepted during staggered frontend/API delivery.
  source?: string;
  match?: string;
  cache_key?: string;
  method?: string;
}

type CompatibleSectionRow = Omit<AeroSectionSolveRow, "prediction_receipt"> & {
  prediction_receipt?: FoilMemoryPredictionReceipt;
  reuse_receipt?: FoilMemoryPredictionReceipt;
  memory_persisted?: boolean;
  publication_persisted?: boolean;
};

interface CompatibleFoilMemorySummary {
  exact_hits?: number;
  solver_runs?: number;
  publication_failures?: number;
  approximate_hits?: number;
  shared?: boolean;
  store_kind?: string;
}

type CompatibleSectionSolveResponse = Omit<AeroSectionSolveResponse, "foil_memory"> & {
  foil_memory?: CompatibleFoilMemorySummary;
  global_foil_memory?: CompatibleFoilMemorySummary;
};

export interface FoilMemoryStats {
  globalExactHits: number;
  globalApproximateHits: number;
  exactReuseHits: number;
  solverRuns: number;
  publicationFailures: number;
  legacyExactHits: number;
  shared: boolean | null;
  storeKind: string | null;
  hasReceiptContract: boolean;
}

export interface SectionRowSource {
  label: "global exact" | "global approximate" | "solver run" | "exact reuse" | "calculated";
  detail: string | null;
  isGlobalExact: boolean;
}

export interface AircraftSectionModelPresentation {
  label: string;
  detail: string;
  isNeural: boolean;
  instanceId: string | null;
  inputSliderNames: string[];
}

function finiteCount(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? Math.floor(value)
    : null;
}

function compatibleReceipt(row: AeroSectionSolveRow): FoilMemoryPredictionReceipt | null {
  const compatible = row as CompatibleSectionRow;
  return compatible.prediction_receipt ?? compatible.reuse_receipt ?? null;
}

function normalizedReceiptKind(receipt: FoilMemoryPredictionReceipt): string {
  return String(receipt.kind ?? receipt.source ?? receipt.match ?? "").trim().toLowerCase();
}

export function sectionRowSource(row: AeroSectionSolveRow): SectionRowSource {
  const receipt = compatibleReceipt(row);
  if (receipt) {
    const kind = normalizedReceiptKind(receipt);
    const exact = receipt.exact === true || kind.includes("exact");
    const approximate = receipt.approximate === true || kind.includes("interpolation");
    const global = kind === "global-exact"
      || kind === "global_exact"
      || kind === "shared-exact";
    const detail = [
      receipt.scope,
      receipt.observation_key ?? receipt.cache_key,
      receipt.solver_backend,
      receipt.solver_build_id,
      receipt.method,
      approximate && (row as CompatibleSectionRow).memory_persisted === false
        ? "ephemeral prediction · source evidence stored"
        : (row as CompatibleSectionRow).memory_persisted === false ? "not stored" : null,
      (row as CompatibleSectionRow).memory_persisted === true
        && (row as CompatibleSectionRow).publication_persisted === false
        && (kind === "solver-run" || kind === "execution-cache-exact")
        ? "memory write not recorded"
        : null,
    ].filter((value): value is string => typeof value === "string" && value.length > 0).join(" · ") || null;
    if (approximate) return { label: "global approximate", detail, isGlobalExact: false };
    if (kind.includes("solver") || kind.includes("run")) return { label: "solver run", detail, isGlobalExact: false };
    if (exact && global) return { label: "global exact", detail, isGlobalExact: true };
    if (exact) return { label: "exact reuse", detail, isGlobalExact: false };
  }
  if (row.cached === true) {
    return { label: "exact reuse", detail: row.cache_key ?? null, isGlobalExact: false };
  }
  return { label: row.status === "done" ? "calculated" : "solver run", detail: row.cache_key ?? null, isGlobalExact: false };
}

export function foilMemoryStats(response: AeroSectionSolveResponse | null): FoilMemoryStats {
  if (!response) {
    return {
      globalExactHits: 0,
      globalApproximateHits: 0,
      exactReuseHits: 0,
      solverRuns: 0,
      publicationFailures: 0,
      legacyExactHits: 0,
      shared: null,
      storeKind: null,
      hasReceiptContract: false,
    };
  }
  const compatible = response as CompatibleSectionSolveResponse;
  const summary = compatible.foil_memory ?? compatible.global_foil_memory;
  const rowSources = response.rows.map(sectionRowSource);
  const rowGlobalExactHits = rowSources.filter((source) => source.isGlobalExact).length;
  const rowGlobalApproximateHits = rowSources.filter((source) => source.label === "global approximate").length;
  const rowExactReuseHits = rowSources.filter((source) => source.label === "exact reuse").length;
  const rowSolverRuns = rowSources.filter((source) => source.label === "solver run" || source.label === "calculated").length;
  const explicitReceiptRows = response.rows.filter((row) => compatibleReceipt(row) != null).length;
  const globalExactHits = explicitReceiptRows
    ? rowGlobalExactHits
    : finiteCount(summary?.exact_hits) ?? 0;
  const solverRuns = finiteCount(response.solver_runs)
    ?? (explicitReceiptRows ? rowSolverRuns : finiteCount(summary?.solver_runs) ?? rowSolverRuns);
  return {
    globalExactHits,
    globalApproximateHits: explicitReceiptRows
      ? rowGlobalApproximateHits
      : finiteCount(summary?.approximate_hits) ?? 0,
    exactReuseHits: rowExactReuseHits,
    solverRuns,
    publicationFailures: finiteCount(summary?.publication_failures) ?? 0,
    legacyExactHits: response.rows.filter((row) => compatibleReceipt(row) == null && row.cached === true).length,
    shared: typeof summary?.shared === "boolean" ? summary.shared : null,
    storeKind: typeof summary?.store_kind === "string" && summary.store_kind.trim() ? summary.store_kind : null,
    hasReceiptContract: explicitReceiptRows > 0 || summary != null,
  };
}

function recordValue(source: Record<string, unknown>, key: string): unknown {
  const value = source[key];
  if (value != null) return value;
  const model = source.model;
  return model && typeof model === "object" ? (model as Record<string, unknown>)[key] : undefined;
}

export function aircraftSectionModelPresentation(
  source: AeroLiftingLineSolveResponse["section_model"] | Record<string, unknown> | null | undefined,
): AircraftSectionModelPresentation {
  const model = source && typeof source === "object" ? source as Record<string, unknown> : {};
  const kind = String(recordValue(model, "kind") ?? recordValue(model, "format") ?? "").trim();
  const scope = String(recordValue(model, "model_scope") ?? "").trim();
  const instance = recordValue(model, "model_instance_id");
  const sliderNames = recordValue(model, "section_shape_axis_names")
    ?? recordValue(model, "input_slider_names");
  const shapeDimensionValue = recordValue(model, "shape_dimension")
    ?? (model.counts && typeof model.counts === "object"
      ? (model.counts as Record<string, unknown>).shape_dimension
      : undefined);
  const uncertainty = recordValue(model, "uncertainty_kind");
  const isNeural = /neural|mlp|\bnn\b/i.test(kind);
  const isLegacyGp = /gaussian|\bgp\b/i.test(kind);
  const inputSliderNames = Array.isArray(sliderNames)
    ? sliderNames.filter((value): value is string => typeof value === "string")
    : [];
  const shapeDimension = typeof shapeDimensionValue === "number" && Number.isFinite(shapeDimensionValue)
    ? Math.max(0, Math.floor(shapeDimensionValue))
    : null;
  const detailParts = [
    scope === "aircraft" ? "one aircraft instance" : null,
    inputSliderNames.length
      ? `${inputSliderNames.length} section-shape axes${shapeDimension ? ` → ${shapeDimension}-D contour` : " → contour input"}`
      : shapeDimension ? `${shapeDimension}-D contour input` : null,
    typeof uncertainty === "string" && uncertainty ? `${uncertainty} uncertainty` : null,
  ].filter((value): value is string => value != null);
  return {
    label: isNeural ? "aircraft NN" : isLegacyGp ? "legacy GP adapter" : kind ? "aircraft model" : "not fitted",
    detail: detailParts.join(" · ") || (isLegacyGp
      ? "compatible with the prior campaign model"
      : "one versioned model over this aircraft's mapped section geometry"),
    isNeural,
    instanceId: typeof instance === "string" && instance ? instance : null,
    inputSliderNames,
  };
}
